
// 5. TimelineCompactView Component
// src/components/Timeline/TimelineCompactView.tsx
import React from 'react';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  Paper,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  Hotel as HotelIcon,
  DirectionsCar as TransportIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { format, isSameDay } from 'date-fns';
import TimelineEventCard from './components/TimelineEvent';
import { Activity, Accommodation, Transportation, TimelineEvent, TripCollaborator } from './types';

interface TimelineCompactViewProps {
  days: Date[];
  isPastTrip: boolean;
  currentTab: number;
  onTabChange: (index: number) => void;
  getActivitiesForDay: (day: Date) => Activity[];
  getAccommodationsForDay: (day: Date) => Accommodation[];
  getTransportationForDay: (day: Date) => Transportation[];
  onEditActivity: (activityId: string) => void;
  onDeleteActivity: (activityId: string) => void;
  openImageDialog: (images: string[], startIndex?: number) => void;
  currentUser?: {
    id: string;
    name: string;
    avatar?: string;
  };
  collaborators?: TripCollaborator[];
}

const TimelineCompactView: React.FC<TimelineCompactViewProps> = ({
  days,
  isPastTrip,
  currentTab,
  onTabChange,
  getActivitiesForDay,
  getAccommodationsForDay,
  getTransportationForDay,
  onEditActivity,
  onDeleteActivity,
  openImageDialog,
  currentUser,
  collaborators
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    onTabChange(newValue);
  };
  
  return (
    <Box>
      <Tabs 
        value={currentTab} 
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {days.map((day, index) => {
          const isToday = isSameDay(day, new Date());
          return (
            <Tab 
              key={day.toISOString()} 
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="body2" component="span">
                    {format(day, 'EEE')}
                  </Typography>
                  <Box 
                    sx={{ 
                      bgcolor: isToday ? 'primary.main' : 'transparent',
                      color: isToday ? 'white' : 'inherit',
                      borderRadius: '50%',
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="body2" component="span">
                      {format(day, 'd')}
                    </Typography>
                  </Box>
                </Box>
              }
              id={`day-tab-${index}`}
              aria-controls={`day-panel-${index}`}
            />
          );
        })}
      </Tabs>
      
      <Box sx={{ mt: 2, mx: 'auto', maxWidth: 800 }}>
        {days.map((day, index) => (
          <div
            key={day.toISOString()}
            role="tabpanel"
            hidden={currentTab !== index}
            id={`day-panel-${index}`}
            aria-labelledby={`day-tab-${index}`}
          >
            {currentTab === index && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" component="div" sx={{ mr: 1 }}>
                    {format(day, 'EEEE, MMMM d, yyyy')}
                  </Typography>
                  {isSameDay(day, new Date()) && (
                    <Chip 
                      label="Today" 
                      color="primary" 
                      size="small" 
                    />
                  )}
                </Box>
                
                {getAccommodationsForDay(day).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Accommodation
                    </Typography>
                    {getAccommodationsForDay(day).map(accommodation => (
                      <Paper key={accommodation.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <HotelIcon sx={{ mr: 1 }} color="primary" />
                          <Box>
                            <Typography variant="body1">{accommodation.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {accommodation.address}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {accommodation.images && accommodation.images.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', overflowX: 'auto', pb: 1 }}>
                            {accommodation.images.slice(0, isMobile ? 3 : 4).map((img: string, index: number) => (
                              <Box 
                                key={index}
                                sx={{ 
                                  width: { xs: 60, sm: 70 }, 
                                  height: { xs: 60, sm: 70 }, 
                                  flexShrink: 0,
                                  mr: 1, 
                                  backgroundImage: `url(${img})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  borderRadius: 1,
                                  cursor: 'pointer'
                                }}
                                onClick={() => openImageDialog(accommodation.images || [], index)}
                              />
                            ))}
                            {accommodation.images.length > (isMobile ? 3 : 4) && (
                              <Box 
                                sx={{ 
                                  width: { xs: 60, sm: 70 }, 
                                  height: { xs: 60, sm: 70 }, 
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(0,0,0,0.1)',
                                  borderRadius: 1,
                                  cursor: 'pointer'
                                }}
                                onClick={() => openImageDialog(accommodation.images || [])}
                              >
                                <Typography variant="caption" fontWeight="bold">
                                  +{accommodation.images.length - (isMobile ? 3 : 4)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                )}
                
                {getTransportationForDay(day).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Transportation
                    </Typography>
                    {getTransportationForDay(day).map(transport => (
                      <Paper key={transport.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TransportIcon sx={{ mr: 1 }} color="primary" />
                          <Box>
                            <Typography variant="body1">
                              {transport.from} to {transport.to}
                            </Typography>
                            {transport.departureTime && (
                              <Typography variant="body2" color="text.secondary">
                                {format(new Date(transport.departureTime), 'h:mm a')}
                                {transport.arrivalTime && ` - ${format(new Date(transport.arrivalTime), 'h:mm a')}`}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        
                        {transport.images && transport.images.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', overflowX: 'auto', pb: 1 }}>
                            {transport.images.slice(0, isMobile ? 3 : 4).map((img: string, index: number) => (
                              <Box 
                                key={index}
                                sx={{ 
                                  width: { xs: 60, sm: 70 }, 
                                  height: { xs: 60, sm: 70 }, 
                                  flexShrink: 0,
                                  mr: 1, 
                                  backgroundImage: `url(${img})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  borderRadius: 1,
                                  cursor: 'pointer'
                                }}
                                onClick={() => openImageDialog(transport.images || [], index)}
                              />
                            ))}
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                )}
                
                <Typography variant="subtitle1" gutterBottom>
                  Activities
                </Typography>
                
                {getActivitiesForDay(day).length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No activities planned for this day.
                  </Typography>
                ) : (
                  getActivitiesForDay(day).map(activity => (
                    <Box key={activity.id} sx={{ mb: 2 }}>
                      <TimelineEventCard
                        event={{
                          id: activity.id,
                          type: 'activity',
                          date: day,
                          title: activity.title,
                          description: activity.description,
                          images: activity.images,
                          comments: [],
                          reference: activity
                        }}
                        isPastTrip={isPastTrip}
                        currentUser={currentUser}
                        collaborators={collaborators || []}
                        onOpenImageDialog={openImageDialog}
                        onShowCollaborators={() => {}}
                      />
                    </Box>
                  ))
                )}
                
                {isPastTrip && (
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 1.5, 
                      mt: 2, 
                      display: 'flex', 
                      alignItems: 'center', 
                      backgroundColor: 'background.paper'
                    }}
                  >
                    <LockIcon fontSize="small" sx={{ mr: 1 }} color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Editing is disabled for past trips
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}
          </div>
        ))}
      </Box>
    </Box>
  );
};

export default TimelineCompactView;